import { useCallback, useContext, useEffect, useState } from 'react';
import { createContext } from 'react';

import { ethers } from 'ethers';
import { truncateEthAddress } from '../utils/wallet_utils';

export type UserDataValueType = {
  wallet_address: string;
  user_name: string;
  connected: boolean;
};

export type UserContextType = UserDataValueType & {
  connect: () => Promise<boolean>;
  disconnect: () => void;
  signMessage: (message: string, hashMessage: string) => Promise<string>;
};

const defaultValue = {
  wallet_address: 'Not connected',
  user_name: 'Not connected',
  connected: false,
  owned_room_ids: [],
  owned_rooms: [],
  connect: () =>
    new Promise<boolean>((_, reject) =>
      reject(new Error('Wallet not connected.'))
    ),
  disconnect: () => {},
  signMessage: (_message: string, _hashMessage: string) =>
    new Promise<string>((_, reject) =>
      reject(new Error('Wallet not connected.'))
    ),
};

export const UserContext = createContext<UserContextType>(defaultValue);

type Props = {
  children: any;
};

export const UserContextProvider = ({ children }: Props) => {
  const [userData, setUserData] = useState<UserDataValueType>(defaultValue);

  const [provider, setProvider] = useState<
    ethers.providers.Web3Provider | undefined
  >();

  // NextJS doesn't have the window.ethereum object.
  useEffect(
    () =>
      window?.ethereum &&
      setProvider(new ethers.providers.Web3Provider(window?.ethereum)),
    []
  );

  const [signer, setSigner] = useState<ethers.Signer | undefined>();

  const connect = useCallback(async () => {
    if (provider === undefined) {
      return false;
    }

    try {
      await provider.send('eth_requestAccounts', []);

      const signer = provider.getSigner();

      setSigner(signer);

      const wallet_address = await signer.getAddress();

      setUserData({
        ...defaultValue,
        wallet_address,
        user_name: truncateEthAddress(wallet_address),
        connected: true,
      });
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  }, [provider]);

  const disconnect = useCallback(async () => {
    setSigner(undefined);
    setUserData(defaultValue);
  }, []);

  const signMessage = useCallback(
    async (message: string, hashMessage: string) => {
      if (!signer) {
        throw new Error('Wallet is not connected.');
      }

      const signature = await signer.signMessage(
        `${message} - Signature: ${hashMessage}`
      );

      return `${signature}`;
    },
    [signer]
  );

  return (
    <UserContext.Provider
      value={{
        ...userData,
        connect,
        disconnect,
        signMessage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export function useUserContext() {
  return useContext(UserContext);
}
